import {
  assetAllIdsResultSchema,
  assetCountsSchema,
  assetPageSchema,
  assetPositionSchema,
  folderFacetListSchema,
  folderTreeResultSchema,
  tagFacetListSchema,
  type AssetCounts,
  type AssetPage,
  type AssetPosition,
  type AssetQuery,
  type FolderFacetList,
  type FolderTreeResult,
  type TagFacetList,
} from '@xphoto/shared';
import { apiFetch } from '../lib/http';
import { withSlotParam } from '../lib/activeSlot';

/**
 * Client typé de l'API galerie (T2b). Consomme les contrats partagés
 * `@xphoto/shared` — les DTO ne sont JAMAIS redéfinis ici, seulement re-parsés
 * (« never trust the client » vaut aussi pour ses propres réponses).
 *
 * Chaque appel valide la réponse via son schéma Zod : un backend divergent
 * lève, plutôt que de propager des données mal typées dans l'UI.
 */

/** Erreur d'appel API : code d'erreur normalisé (`NOT_FOUND`, `VALIDATION`…) + statut HTTP. */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    /** Secondes avant nouvel essai (en-tête `Retry-After`), si fourni par le serveur (429). */
    public readonly retryAfterSeconds: number | null = null,
  ) {
    super(`API ${status} ${code}`);
    this.name = 'ApiError';
  }
}

/** Sous-ensemble d'`AssetQuery` piloté par l'UI (pagination gérée à part). */
export type GalleryFilters = Partial<Omit<AssetQuery, 'offset' | 'limit'>>;

/**
 * Vrai si `(key, value)` doit être OMIS de la query-string (valeur « vide »). Le
 * `folder` fait EXCEPTION : la chaîne vide `''` y est SIGNIFICATIVE (racine, en
 * navigation `folderDirect`) → on la sérialise (`folder=`), sinon le filtre « fichiers
 * de la racine » se perdrait. Les autres clés à valeur vide restent omises (URL propre).
 */
function isEmptyFilterEntry(key: string, value: unknown): boolean {
  if (value === undefined || value === null) return true;
  return value === '' && key !== 'folder';
}

/** Sérialise les filtres + fenêtre en query-string, en omettant les valeurs vides. */
export function buildAssetSearchParams(
  filters: GalleryFilters,
  offset: number,
  limit: number,
): URLSearchParams {
  const sp = new URLSearchParams();
  sp.set('offset', String(offset));
  sp.set('limit', String(limit));
  for (const [key, value] of Object.entries(filters)) {
    if (isEmptyFilterEntry(key, value)) continue;
    // Filtre multi-valeurs (ex. tagIds) : sérialisé en CSV, omis si vide.
    if (Array.isArray(value)) {
      if (value.length > 0) sp.set(key, value.join(','));
      continue;
    }
    sp.set(key, String(value));
  }
  return sp;
}

/**
 * Parse l'en-tête `Retry-After` en SECONDES : soit un delta entier (`120`), soit une
 * date HTTP (RFC 7231). `null` si absent/illisible. Toujours ≥ 0. Sert au compte à
 * rebours d'auto-réessai sur 429 (limiteur de débit `@fastify/rate-limit`).
 */
export function parseRetryAfter(value: string | null): number | null {
  if (value === null || value.trim() === '') return null;
  const secs = Number(value);
  if (Number.isFinite(secs)) return Math.max(0, Math.ceil(secs));
  const when = Date.parse(value);
  if (!Number.isNaN(when)) return Math.max(0, Math.ceil((when - Date.now()) / 1000));
  return null;
}

async function readJson(res: Response): Promise<unknown> {
  if (!res.ok) {
    let code = 'UNKNOWN';
    try {
      const body = (await res.json()) as { error?: { code?: string } };
      if (typeof body?.error?.code === 'string') code = body.error.code;
    } catch {
      // Corps non-JSON (ex. 429 du limiteur) : on garde le code générique.
    }
    throw new ApiError(res.status, code, parseRetryAfter(res.headers.get('Retry-After')));
  }
  return res.json();
}

/** `GET /api/libraries/:id/assets` — page fenêtrée triée/filtrée. */
export async function fetchAssetPage(
  libraryId: string,
  filters: GalleryFilters,
  offset: number,
  limit: number,
  signal?: AbortSignal,
): Promise<AssetPage> {
  const sp = buildAssetSearchParams(filters, offset, limit);
  const res = await apiFetch(`/api/libraries/${libraryId}/assets?${sp.toString()}`, {
    signal: signal ?? null,
  });
  return assetPageSchema.parse(await readJson(res));
}

/**
 * `GET /api/libraries/:id/assets/:assetId/position` — rang (index 0-based) de
 * l'asset dans l'ensemble ORDONNÉ courant (mêmes filtres/tri que la liste) + total.
 * Sert à la visionneuse pour naviguer préc./suiv. de façon FIABLE sur TOUT
 * l'ensemble (ouverture à froid ou hors fenêtre chargée). Le serveur renvoie 404 si
 * l'asset n'appartient pas à l'ensemble → propagé en `ApiError` (statut 404).
 */
export async function fetchAssetPosition(
  libraryId: string,
  assetId: string,
  filters: GalleryFilters,
  signal?: AbortSignal,
): Promise<AssetPosition> {
  // Mêmes clés de query que la liste (le serveur réutilise le même query-builder),
  // sans pagination (offset/limit ignorés pour le rang).
  const qs = buildFilterSearchParams(filters).toString();
  const url = `/api/libraries/${libraryId}/assets/${assetId}/position${qs ? `?${qs}` : ''}`;
  const res = await apiFetch(url, { signal: signal ?? null });
  return assetPositionSchema.parse(await readJson(res));
}

/**
 * Sérialise les seuls FILTRES (sans pagination) en query-string, en omettant les
 * valeurs vides et en encodant le multi-états en CSV. Partagé par `fetchAssetPosition`
 * et `fetchAllAssetIds` (le serveur réutilise le même query-builder que la liste).
 */
function buildFilterSearchParams(filters: GalleryFilters): URLSearchParams {
  const sp = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (isEmptyFilterEntry(key, value)) continue;
    if (Array.isArray(value)) {
      if (value.length > 0) sp.set(key, value.join(','));
      continue;
    }
    sp.set(key, String(value));
  }
  return sp;
}

/**
 * `GET /api/libraries/:id/assets/all-ids` — liste EXHAUSTIVE des ids des assets
 * correspondant au FILTRE COURANT (mêmes filtres que la liste, sans pagination). Sert
 * au « Tout sélectionner » de la galerie : sélectionner l'ENSEMBLE du groupe
 * (dossier/tag/filtre/à-traiter) — des milliers d'assets, pas seulement les tuiles
 * chargées — pour agir en masse. Réponse re-parsée via son schéma Zod.
 */
export async function fetchAllAssetIds(
  libraryId: string,
  filters: GalleryFilters,
  signal?: AbortSignal,
): Promise<string[]> {
  const qs = buildFilterSearchParams(filters).toString();
  const url = `/api/libraries/${libraryId}/assets/all-ids${qs ? `?${qs}` : ''}`;
  const res = await apiFetch(url, { signal: signal ?? null });
  return assetAllIdsResultSchema.parse(await readJson(res)).ids;
}

/**
 * `GET /api/libraries/:id/folders` — SOUS-DOSSIERS DIRECTS d'un dossier (navigation
 * hiérarchique façon explorateur). `parent` = chemin du dossier courant dont on veut
 * les sous-dossiers directs (défaut `''` = racine = dossiers de 1er niveau,
 * comportement historique). Chaque `path` renvoyé est alors le chemin COMPLET du
 * sous-dossier (ex. `parent='2024'` → `2024/vacances`).
 */
export async function fetchFolders(
  libraryId: string,
  parent = '',
  signal?: AbortSignal,
): Promise<FolderFacetList> {
  const qs = parent !== '' ? `?parent=${encodeURIComponent(parent)}` : '';
  const res = await apiFetch(`/api/libraries/${libraryId}/folders${qs}`, {
    signal: signal ?? null,
  });
  return folderFacetListSchema.parse(await readJson(res));
}

/**
 * `GET /api/libraries/:id/folder-tree` — ARBRE DES DOSSIERS (XPHOTO-32, `ADR-0013`
 * amendé) : sous-répertoires DIRECTS du dossier `parent` (défaut `''` = racine), lus
 * dans l'index des dossiers et fusionnés avec les facettes SQL (compteurs + couverture).
 *
 * Différence avec `fetchFolders` (facettes seules) : cette route montre AUSSI les
 * répertoires VIDES, invisibles pour les facettes puisqu'elles sont dérivées du chemin
 * des photos. C'est la source de l'onglet Dossiers ; `fetchFolders` reste utilisée par
 * les écrans qui n'ont besoin que des facettes (EXIF).
 */
export async function fetchFolderTree(
  libraryId: string,
  parent = '',
  signal?: AbortSignal,
): Promise<FolderTreeResult> {
  const qs = parent !== '' ? `?parent=${encodeURIComponent(parent)}` : '';
  const res = await apiFetch(`/api/libraries/${libraryId}/folder-tree${qs}`, {
    signal: signal ?? null,
  });
  return folderTreeResultSchema.parse(await readJson(res));
}

/** `GET /api/libraries/:id/tags` — facettes de l'onglet Tags (peut être vide avant T5). */
export async function fetchTags(libraryId: string, signal?: AbortSignal): Promise<TagFacetList> {
  const res = await apiFetch(`/api/libraries/${libraryId}/tags`, { signal: signal ?? null });
  return tagFacetListSchema.parse(await readJson(res));
}

/** `GET /api/libraries/:id/counts` — compteurs de la barre d'outils. */
export async function fetchCounts(libraryId: string, signal?: AbortSignal): Promise<AssetCounts> {
  const res = await apiFetch(`/api/libraries/${libraryId}/counts`, { signal: signal ?? null });
  return assetCountsSchema.parse(await readJson(res));
}

/**
 * `POST /api/libraries/:id/scan` — enfile un job de scan (endpoint T1). Réponse
 * volontairement non typée ici (le contrat `job` complet relève du centre de
 * jobs) : on ne consomme que le succès de l'enfilement.
 */
export async function enqueueScan(libraryId: string, dryRun = false): Promise<void> {
  const res = await apiFetch(`/api/libraries/${libraryId}/scan`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ dryRun }),
  });
  await readJson(res);
}

/**
 * URL de la vignette d'un asset (taille servie par l'API : 256 ou 1024). URL
 * browser-native (`<img src>`) : le slot actif passe en `?slot=N` (l'en-tête
 * `X-Active-Slot` n'est pas posable ici).
 *
 * `rev` (optionnel) = CACHE-BUSTER : la vignette est servie `Cache-Control: immutable`
 * (~1 an) sous la clé `identity_hash + taille`, INCHANGÉE par une rotation (l'outil
 * « Pivoter » n'écrit que le tag EXIF Orientation, pixels intacts). Sans buster, l'ancienne
 * orientation resterait affichée. On appose donc `&v=<rev>` (préfixe du `file_hash`, qui
 * change à chaque écriture EXIF) → nouvelle URL après rotation. Ignoré par le serveur (le
 * contenu reste déterminé par l'identity_hash). Absent/vide ⇒ URL non bustée (cache normal).
 */
export function thumbnailUrl(assetId: string, size: 256 | 1024, rev?: string): string {
  const base = `/api/assets/${assetId}/thumbnail?size=${size}`;
  const withRev = rev !== undefined && rev !== '' ? `${base}&v=${encodeURIComponent(rev)}` : base;
  return withSlotParam(withRev);
}
